<template>
  <div class="learning-data-tab" v-loading="loading">
    <!-- 单词总量统计 -->
    <el-card class="stat-card" shadow="never">
      <div slot="header" class="card-header">
        <span>单词学习统计</span>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          size="small"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          @change="handleDateChange"
        />
      </div>

      <div class="stat-grid">
        <div class="stat-item">
          <div class="stat-icon" style="background: #ecf5ff; color: #409eff">
            <i class="el-icon-document"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ learningData.totalWords || 0 }}</div>
            <div class="stat-label">累计学习单词</div>
          </div>
        </div>

        <div class="stat-item">
          <div class="stat-icon" style="background: #f0f9ff; color: #67c23a">
            <i class="el-icon-success"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ learningData.masteredWords || 0 }}</div>
            <div class="stat-label">已掌握单词</div>
          </div>
        </div>

        <div class="stat-item">
          <div class="stat-icon" style="background: #fef0f0; color: #f56c6c">
            <i class="el-icon-refresh"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ learningData.reviewWords || 0 }}</div>
            <div class="stat-label">复习中单词</div>
          </div>
        </div>

        <div class="stat-item">
          <div class="stat-icon" style="background: #fdf6ec; color: #e6a23c">
            <i class="el-icon-clock"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ learningData.newWords || 0 }}</div>
            <div class="stat-label">待学习单词</div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 掌握程度分布 -->
    <el-card class="chart-card" shadow="never">
      <div slot="header" class="card-header">
        <span>单词掌握程度分布</span>
      </div>
      <div ref="masteryChart" class="chart-container"></div>
    </el-card>

    <!-- 学习详情列表 -->
    <el-card class="detail-card" shadow="never">
      <div slot="header" class="card-header">
        <span>最近学习单词</span>
      </div>
      <el-table
        :data="recentWords"
        style="width: 100%"
        max-height="400"
      >
        <el-table-column prop="word" label="单词" min-width="120" />
        <el-table-column prop="translation" label="翻译" min-width="150" />
        <el-table-column prop="reviewCount" label="复习次数" width="100" align="center" />
        <el-table-column prop="masteryLevel" label="掌握程度" width="120" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="masteryTagType(scope.row.masteryLevel)"
              size="small"
            >
              {{ masteryText(scope.row.masteryLevel) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="lastReviewTime" label="最后复习" min-width="160" />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { mapActions } from 'vuex';

export default {
  name: 'LearningDataTab',
  props: {
    studentId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      dateRange: null,
      learningData: {},
      recentWords: [],
      masteryChart: null,
    };
  },
  created() {
    this.loadLearningData();
  },
  mounted() {
    this.initMasteryChart();
  },
  beforeDestroy() {
    if (this.masteryChart) {
      this.masteryChart.dispose();
    }
  },
  methods: {
    ...mapActions('student', ['fetchLearningDataWithWords']),

    /**
     * 加载学习数据 (优化版:一次接口调用)
     */
    async loadLearningData() {
      this.loading = true;
      try {
        const params = {
          studentId: this.studentId,
          startDate: this.dateRange ? this.dateRange[0] : null,
          endDate: this.dateRange ? this.dateRange[1] : null,
        };
        
        // 一次接口调用同时获取学习数据和最近单词
        const result = await this.fetchLearningDataWithWords(params);
        this.learningData = result.learningData;
        this.recentWords = result.recentWords;
        
        this.updateMasteryChart();
      } catch (error) {
        console.error('加载学习数据失败:', error);
        this.$message.error('加载学习数据失败');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 日期范围改变
     */
    handleDateChange() {
      this.loadLearningData();
    },

    /**
     * 初始化掌握程度图表
     */
    initMasteryChart() {
      this.masteryChart = echarts.init(this.$refs.masteryChart);
      this.updateMasteryChart();
    },

    /**
     * 更新掌握程度图表
     */
    updateMasteryChart() {
      if (!this.masteryChart) return;

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)',
        },
        legend: {
          orient: 'vertical',
          right: 20,
          top: 'center',
        },
        series: [
          {
            name: '掌握程度',
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['40%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2,
            },
            label: {
              show: false,
              position: 'center',
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 20,
                fontWeight: 'bold',
              },
            },
            labelLine: {
              show: false,
            },
            data: [
              { value: this.learningData.masteredWords || 0, name: '已掌握', itemStyle: { color: '#67c23a' } },
              { value: this.learningData.reviewWords || 0, name: '复习中', itemStyle: { color: '#e6a23c' } },
              { value: this.learningData.newWords || 0, name: '待学习', itemStyle: { color: '#909399' } },
            ],
          },
        ],
      };

      this.masteryChart.setOption(option);
    },

    /**
     * 掌握程度标签类型
     * 0-未学习(info), 1-认识(warning), 2-熟悉(primary), 3-掌握(success)
     */
    masteryTagType(level) {
      const types = ['info', 'warning', '', 'success'];
      return types[level] || 'info';
    },

    /**
     * 掌握程度文本
     * 0-未学习, 1-认识, 2-熟悉, 3-掌握
     */
    masteryText(level) {
      const texts = ['未学习', '认识', '熟悉', '掌握'];
      return texts[level] || '未知';
    },
  },
};
</script>

<style scoped>
.learning-data-tab {
  padding: 10px 0;
}

.stat-card,
.chart-card,
.detail-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: #303133;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding: 10px 0;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
  transition: all 0.3s;
}

.stat-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 24px;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 13px;
  color: #909399;
}

.chart-container {
  height: 400px;
  width: 100%;
}

@media (max-width: 1200px) {
  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .stat-grid {
    grid-template-columns: 1fr;
  }
}
</style>
